import './DispatchCardDismiss.css';

/**
 * V9.2.x.3 (2026-06-06) — DispatchCardDismiss.
 *
 * Shared "✕" dismiss button for V9.2 dispatch-action cards
 * (ChaseSequenceCard, PersuasionOutcomeChip, … 11 total). Each card
 * accepts an optional `onDismiss` prop and renders this button when
 * provided. Standardizes:
 *   - Glyph (✕ U+2715)
 *   - Position (top-right corner of the card, absolute)
 *   - Click behavior (stops propagation so a parent click doesn't fire)
 *   - Testid: `${parentTestidPrefix}-dismiss`
 *
 * Per CLAUDE.md identifier-citation rule, the parent card must position
 * itself `relative` for this button's `absolute` positioning to anchor.
 * The 11 V9.2 card CSS files already use `position: relative` implicitly
 * via their padding/flex layout — but the dismiss button's absolute
 * positioning was added in V9.2.x.3 so parents now opt-in by setting
 * `position: relative` on the root `.br-{card}` class.
 */

export interface DispatchCardDismissProps {
  /** Click handler. When undefined, the button does not render. */
  onDismiss?: () => void;
  /**
   * Parent testid prefix. The button's testid is `${testidPrefix}-dismiss`.
   * Falls back to "br-dispatch-card" when omitted (rarely useful — pass
   * the parent's own testid prefix for click-target precision).
   */
  testidPrefix?: string;
  /** ARIA label override. Default: "Dismiss this card". */
  ariaLabel?: string;
}

export function DispatchCardDismiss({
  onDismiss,
  testidPrefix = 'br-dispatch-card',
  ariaLabel = 'Dismiss this card',
}: DispatchCardDismissProps) {
  if (onDismiss === undefined) return null;
  return (
    <button
      type="button"
      className="br-dispatch-card-dismiss"
      data-testid={`${testidPrefix}-dismiss`}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onDismiss();
      }}
    >
      ✕
    </button>
  );
}
